import { defineComponent, reactive, SetupContext, readonly, ref, onMounted, toRefs, watch } from '@vue/composition-api';
import CommonCategory from '@/components/common/common-category.component.vue';                     // カテゴリ選択共通コンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import { OfficialDocumentList, TableHeader } from "@/shared/type";
import { DataOptions } from 'vuetify';
import HttpStatus from 'http-status-codes';
import SystemConst from '@/shared/system-const';
import MessageConst from '@/shared/message-const';
import CommonFunction from '@/shared/common-function';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';
import { TableFilterDefs, TableFilters } from '../common/common-table-filter.component';
import vueRouter from '@/router';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                      // Windowサイズ
    officialDocumentDisplayTableHeaders: TableHeader[];                        // 公文書一覧ヘッダ情報
    officialDocumentListItem: OfficialDocumentList[];                          // 公文書一覧情報
    totalCount: number;                                                        // 公文書一覧の総件数
    pageOptions: DataOptions;                                                  // ページ情報を保存する変数
    isLoading: boolean;                                                        // テーブルがロード中: true ロード中でない: false
    organizationId: string;                                                    // カテゴリ選択で選択された組織ID
    loginUserOrgId: string;                                                    // ログインユーザーの組織ID
    accountDivision: string;                                                   // ログインユーザーのアカウント区分
    isAdminUser: boolean;                                                      // ログインユーザーのシステム管理者フラグ
    isOfficialDocument: boolean;                                               // 公文書権限（true：有、false：無）
    isSystemAdminMode: boolean;                                                // システム管理モードフラグ
    isFromDetail: boolean;                                                     // 詳細画面からの遷移：true, 詳細画面以外からの遷移：false
    filterDefsDisplay: TableFilterDefs;                                        // フィルタ定義（画面表示用）
    filterDefsMyOrg: TableFilterDefs;                                          // フィルタ定義（ログインユーザーの所属組織の一覧を見る場合）
    filterDefsOtherOrg: TableFilterDefs;                                       // フィルタ定義（ログインユーザー所属以外の組織の一覧を見る場合）
    filters: TableFilters;                                                     // フィルタ条件
    isSelectCategory: boolean                                                  // カテゴリ選択フラグ
}

/**
 * 公文書一覧（一般）画面コンポーネント
 */
export default defineComponent({
    components: { CommonCategory, CommonTableFilter, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const tableHeight = SystemConst.TABLE_HEIGHT.OFFICIAL_DOC_LIST;        // テーブル高さ調整用変数
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                  // 1ページあたりの行数候補（10, 50, 100）
        const pageNation = ref<number>(1);                                     // ページ番号（状態管理用）
        const itemsPerPage = ref<number>(linesPerPage[0]);                     // 1ページあたりの行数（状態管理用、デフォルト値：10）
        const sortBy = ref<string[]>([]);                                      // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                                   // ソート順（状態管理用）
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;               // テーブルのデータ取得中に表示するテキスト
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                        // ウィンドウサイズ
            officialDocumentDisplayTableHeaders: [],                           // 公文書一覧ヘッダー
            officialDocumentListItem: [],                                      // 公文書一覧情報
            totalCount: 0,                                                     // 公文書一覧の総件数
            pageOptions: {                                                     // ページ情報を保存する変数
                page: 1, itemsPerPage: linesPerPage[0], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            isLoading: false,                                                  // テーブルがロード中: true ロード中でない: false
            organizationId: '',                                                // カテゴリ選択で選択された組織ID
            loginUserOrgId: '',                                                // ログインユーザーの組織ID
            accountDivision: '',                                               // ログインユーザーのアカウント区分
            isAdminUser: false,                                                // ログインユーザーのシステム管理者フラグ
            isOfficialDocument: false,                                         // 公文書権限（true：有、false：無）
            isSystemAdminMode: false,                                          // システム管理モードフラグ
            isFromDetail: false,                                               // 詳細画面からの遷移：true, 詳細画面以外からの遷移：false
            filterDefsDisplay: {},                                             // フィルタ定義（画面表示用）
            filterDefsMyOrg: {                                                 // フィルタ定義（ログインユーザーの所属組織の一覧を見る場合）
                'freeWord': { title: 'フリーワード', types: 'KeyWordPartialMatch' },
                'issueNumber': { title: '発番', types: 'IssueNumber' },
                'notificationDivision': { title: '一斉／個別', types: 'PullDown' },
                'issueDate': { title: '発出日', types: 'DatePeriod' }
            },
            filterDefsOtherOrg: {                                              // フィルタ定義（ログインユーザー所属以外の組織の一覧を見る場合）
                'freeWord': { title: 'フリーワード', types: 'KeyWordPartialMatch' },
                'issueNumber': { title: '発番', types: 'IssueNumber' },
                'issueDate': { title: '発出日', types: 'DatePeriod' }
            },
            filters: {},                                                       // フィルタ条件
            isSelectCategory: false                                            // カテゴリ選択フラグ
        });
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];         // 公文書一覧のヘッダ色
        const categoryRef = ref<any>(null);                                    // カテゴリー選択
        const filterRef = ref<any>(null);                                      // フィルタコンポーネント
        const commonRef = ref<any>(null);                                      // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）
        const router = vueRouter;                                              // 画面遷移用
        // 公文書一覧ヘッダ情報
        const officialDocumentInitialDisplayTableHeaders: TableHeader[] = [
            {
                text: '',
                value: 'isNew',
                sortable: false,
                width: '4%',
                class: tableHeaderClass
            },
            {
                text: 'タイトル',
                value: 'title',
                width: '67%',
                class: tableHeaderClass
            },
            {
                text: '発出日',
                value: 'issueDate',
                width: '15%',
                class: tableHeaderClass
            }
        ];

        // フィルタバリデーション
        // ・フリーワード
        const freeWordRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.OFFICIAL_DOCUMENT_LIST_MAX_LENGTH.FREE_WORD,
                'フリーワード'
            )
        ];
        // ・発番
        const issueNumberRules = [
            (v: any) =>
                /^[0-9-]*$/.test(v) || CommonFunction.replaceArray(
                    MessageConst.E_REGEX,
                    ['発番', '半角数字、ハイフン（-）']
                ),
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.OFFICIAL_DOCUMENT_LIST_MAX_LENGTH.ISSUE_NUMBER,
                '発番'
            )
        ];

        // カテゴリ選択のアクティブノードを監視する
        const { activeNode } = toRefs(ctx.root.$store.state.officialDocument);
        // カテゴリが選択された場合
        watch(activeNode, () => {
            // storeのアクティブノードから組織IDをstateへ保存する
            state.organizationId = ctx.root.$store.state.officialDocument.activeNode[0];

            // パラメータを初期化し、公文書一覧を取得する
            // フィルタ情報を取得し直す
            selectCategory();
        })

        // システム管理モードフラグを監視
        const { isSystemAdminMode } = toRefs(ctx.root.$store.state);
        // 値が変更された場合
        watch(isSystemAdminMode, () => {
            // boolean値に変換してstateへ保存
            state.isSystemAdminMode = Boolean(ctx.root.$store.state.isSystemAdminMode);
        })

        /**
         * 初期処理
         */
        onMounted(() => {
            // システム管理モードではないまたは、
            // システム管理モードだが書き込み権限がない場合カテゴリを取得する
            if (!state.isSystemAdminMode
                ||
                (
                    state.isSystemAdminMode
                    &&
                    !ctx.root.$store.state.authenticationUser.role.isOfficialDocument
                )
            ) {
                init();
            }
        });
        async function init() {
            // カテゴリ取得
            categoryRef.value.getCategory();

            // 初期表示時にカテゴリ選択のサイズ調整
            onResize();
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // storeからシステム管理モードフラグを取得
            state.isSystemAdminMode = Boolean(ctx.root.$store.state.isSystemAdminMode);

            // システム管理モードかつ、書き込み権限がある場合公文書一覧（管理）画面に遷移
            if (state.isSystemAdminMode
                &&
                ctx.root.$store.state.authenticationUser.role.isOfficialDocument
            ) {
                router.push('/official-documents/admin/list');

                return;
            }

            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '公文書一覧');

            // SAML認証で取得したログインユーザーの情報から以下を取得
            // ・組織ID
            state.loginUserOrgId = ctx.root.$store.state.authenticationUser.organizationId;
            // ・アカウント区分
            state.accountDivision = ctx.root.$store.state.authenticationUser.accountDivision;

            // ログインユーザーのアカウント区分が's1'の場合
            if (state.accountDivision === SystemConst.ACCOUNT_DIVISION.ADMIN) {
                // ログインユーザーのシステム管理者フラグにtrueを設定
                state.isAdminUser = true;
            }

            // storeから公文書権限フラグを取得
            state.isOfficialDocument = ctx.root.$store.state.authenticationUser.role.isOfficialDocument;

            // storeからカテゴリ選択で選択された組織IDを復元
            state.organizationId = ctx.root.$store.state.officialDocument.activeNode[0];

            // storeからフィルター条件をすべて復元
            state.filters = ctx.root.$store.state.officialDocument.filters;

            // storeから前回のページ番号、1ページあたりの行数を復元
            pageNation.value = ctx.root.$store.state.officialDocument.pageNation;
            itemsPerPage.value = ctx.root.$store.state.officialDocument.itemsPerPage;

            // storeから前回のソートキー、ソート順を復元
            sortBy.value = ctx.root.$store.state.officialDocument.sortBy;
            sortDesc.value = ctx.root.$store.state.officialDocument.sortDesc;

            // 詳細画面からの遷移フラグを復元
            state.isFromDetail = ctx.root.$store.state.officialDocument.isFromDetail;

            // フィルタ情報を初期設定
            await initFilter();

            // ヘッダ設定
            state.officialDocumentDisplayTableHeaders = officialDocumentInitialDisplayTableHeaders;

            // 前回のフィルタに対するID格納用の配列
            const filterIdList = [];
            // 前回のフィルタ条件格納用の配列
            const filterParameters = [];

            // state.filtersのプロパティ(key)で以下を繰り返し、有効なフィルタだけを抽出
            for (const key in state.filters) {
                // state.filterのprototypeにkeyがある場合
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    // 有効なフィルタである場合
                    if (val.isEnabled) {
                        // フィルタのID(key)とフィルタ条件を保存
                        filterIdList.push(key);
                        filterParameters.push(val.condition);
                    }
                }
            }

            // 復元された有効なフィルタがある場合
            if (filterIdList.length !== 0) {
                // setAllFiltersで有効なフィルタをすべてstateに設定
                filterRef.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // 公文書一覧情報を設定
                updateList();
            }

            // 詳細画面からの遷移フラグにfalseを設定
            // trueのままだと、詳細画面から遷移後にフィルタを変更したときに1ページ目に戻らなくなるため
            state.isFromDetail = false;
            ctx.root.$store.commit('officialDocument/setFromDetailFlg', false);
        }

        /**
         * フィルタ情報を取得・設定
         */
        async function initFilter() {
            // 画面の初期表示時にカテゴリ取得前であるため
            // カテゴリの組織IDが格納されていない場合、以降の処理を行わない
            if (!state.organizationId) {
                return
            }

            // 公文書一覧フィルタ情報APIでフィルタに表示する情報を取得
            // GET /api/official-documents/filter-conditions
            // パラメータ：
            // ・組織ID（初期表示時は未設定）
            const response = await ctx.root.$store.dispatch('officialDocument/getFilterConditions',
                {
                    organizationId: state.organizationId
                }
            );

            // API通信のレスポンスが正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // カテゴリ選択で自組織を選択した場合に表示するフィルタに値を設定
            if (state.organizationId === state.loginUserOrgId) {
                // 以下の項目にAPIで取得したフィルタ情報を設定
                // ・発番
                state.filterDefsMyOrg['issueNumber'].chkItems = response.data.issueDivision;
                // ・一斉／個別
                state.filterDefsMyOrg['notificationDivision'].items = response.data.notificationDivision;

                // 以下の項目にフィルタバリデーションを設定
                // ・フリーワード
                state.filterDefsMyOrg['freeWord'].validator = freeWordRules;
                // ・発番
                state.filterDefsMyOrg['issueNumber'].validator = issueNumberRules;

                // 画面表示用定義に取得内容を設定
                state.filterDefsDisplay = state.filterDefsMyOrg;
                // カテゴリ選択で他組織を選択した場合に表示するフィルタに値を設定
            } else {
                // 以下の項目にAPIで取得したフィルタ情報を設定
                // ・発番
                state.filterDefsOtherOrg['issueNumber'].chkItems = response.data.issueDivision;

                // 以下の項目にフィルタバリデーションを設定
                // ・フリーワード
                state.filterDefsOtherOrg['freeWord'].validator = freeWordRules;
                // ・発番
                state.filterDefsOtherOrg['issueNumber'].validator = issueNumberRules;

                // 画面表示用定義に取得内容を設定
                state.filterDefsDisplay = state.filterDefsOtherOrg;
            }
        }

        /**
         * 公文書一覧を更新する
         */
        async function updateList() {
            // 画面の初期表示時にカテゴリ取得前であるため
            // カテゴリの組織IDが格納されていない場合、以降の処理を行わない
            if (!state.organizationId) {
                return
            }

            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // 現在のページ情報/ソート情報、フィルタ条件をストアに保存
            // 詳細、登録、編集、削除画面から本画面に戻ってきたときに復元される
            ctx.root.$store.commit('officialDocument/setPage', pageNation.value);
            ctx.root.$store.commit('officialDocument/setItemsPerPage', itemsPerPage.value);
            ctx.root.$store.commit('officialDocument/setFilters', state.filters);
            ctx.root.$store.commit('officialDocument/setSortBy', sortBy.value);
            ctx.root.$store.commit('officialDocument/setSortDesc', sortDesc.value);

            // makeListParametersでAPIパラメータ（組織ID以外）を作成する
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 公文書一覧取得APIでデータを取得する
            // GET /api/official-documents
            // パラメータ：
            // ・組織ID（初期表示時は未設定）
            // ・ページ番号
            // ・1ページあたりの行数
            // ・ソートキー
            // ・ソート順
            // ・フィルタ条件
            const response = await ctx.root.$store.dispatch('officialDocument/getDocuments',
                {
                    organizationId: state.organizationId,
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信のレスポンスが正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 取得した公文書一覧のデータを設定する
            state.officialDocumentListItem = response.data.officialDocuments;

            // 取得した公文書一覧の総件数を設定する
            state.totalCount = response.data.totalCount;

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * ページ情報/ソート情報変更に対するイベント
         * @param options ページ情報/ソート情報
         */
        function updateOptions(options: DataOptions) {
            // カテゴリ選択時以外の場合
            if (!state.isSelectCategory) {
                // ページ情報をstateへ保存する
                state.pageOptions = options;

                // optionsの中身と比較して、いずれも変更されていなかった場合
                if (pageNation.value === options.page &&
                    itemsPerPage.value === options.itemsPerPage &&
                    sortBy.value === options.sortBy &&
                    sortDesc.value === options.sortDesc
                ) {
                    // 処理を終了する
                    return;
                }

                // ページ番号、1ページあたりの行数を設定する
                pageNation.value = options.page;
                itemsPerPage.value = options.itemsPerPage;

                // ソートキー、ソート順を設定する
                sortBy.value = options.sortBy;
                sortDesc.value = options.sortDesc;

                // 公文書一覧を更新する
                updateList();
            }
        }

        /**
         * フィルタ設定によるイベント
         * 
         * @param filters フィルタ条件
         */
        function updateFilters(filters: TableFilters) {
            // filtersをstateへ保存する
            state.filters = filters;

            // 詳細画面からの遷移時以外
            if (!state.isFromDetail) {
                // ページ番号を1へ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // 公文書一覧を更新する
            updateList();
        }

        /**
         * カテゴリ選択時のイベント
         */
        async function selectCategory() {
            // カテゴリ選択フラグにtrueを設定する
            state.isSelectCategory = true;

            // ページ情報を初期化する
            state.pageOptions.page = 1;
            pageNation.value = 1;
            state.pageOptions.itemsPerPage = linesPerPage[0];
            itemsPerPage.value = linesPerPage[0];

            // ソート情報を初期化する
            state.pageOptions.sortBy = [];
            sortBy.value = [];
            state.pageOptions.sortDesc = [];
            sortDesc.value = [];

            // フィルタ条件を初期化する
            state.filters = {};

            // 表示されているフィルタ条件を削除する
            // フィルタ条件変更を契機にupdateFiltersが発火し、
            // 上で初期化したパラメータにより公文書一覧を取得する
            filterRef.value.allClear();

            // 選択した組織がログインユーザーの所属する組織かつ、書き込み権限がある場合
            if (state.organizationId === ctx.root.$store.state.authenticationUser.organizationId
                &&
                ctx.root.$store.state.authenticationUser.role.isOfficialDocument
            ) {
                // 公文書一覧（管理）画面へ遷移
                router.push('/official-documents/admin/list');

                return;
            }

            // 選択したカテゴリに合わせてフィルタを取得し直す
            await initFilter();

            // カテゴリ選択フラグにtrueを設定する
            state.isSelectCategory = false;
        }

        /**
         * 詳細画面への遷移
         * 
         * @param officialDocumentId 公文書ID
         */
        function navigationDetail(officialDocumentId: string) {
            // 詳細画面からの遷移フラグをtrueとする
            // 詳細画面から戻った時にページ情報/ソート情報、フィルタ条件が初期化されないようにするため
            ctx.root.$store.commit('officialDocument/setFromDetailFlg', true);
            // 公文書詳細画面へ遷移する
            router.push(`/official-documents/detail/${officialDocumentId}`);
        }

        /**
         * 公文書一覧添付ファイルダウンロード
         * 
         * @param officialDocumentId 公文書ID
         * @param fileSequence ファイルシーケンス
         */
        async function download(officialDocumentId: string, fileSequence: number) {
            // 公文書一覧添付ファイルダウンロードAPIでファイルをダウンロードする
            // GET /api/official-documents/{officialDocumentId}/{fileSequence}
            // ・officialDocumentId: 公文書ID
            // ・fileSequence:ファイルシーケンス
            const response = await ctx.root.$store.dispatch('officialDocument/download',
                {
                    officialDocumentId: officialDocumentId,
                    fileSequence: fileSequence
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            // 現在のウィンドウの幅、高さを保存
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };

            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);
        }

        return {
            tableHeight,
            linesPerPage,
            pageNation,
            itemsPerPage,
            sortBy,
            sortDesc,
            tableLoadingText,
            state: readonly(state),
            categoryRef,
            filterRef,
            commonRef,
            updateOptions,
            updateFilters,
            navigationDetail,
            download,
            onResize
        };
    }
});
